import {
  GetSolutionsQuery,
  proStudioUrqlGql as urqlGql,
} from '@pypestream/api-services';
import { throwErrorWithId } from '@pypestream/utils';

import {
  buildSelectedSolution,
  checkForErrors,
  pickDraftVersion,
} from '../../utils';
import {
  ProStudioContext,
  ProStudioEvents,
  SelectedSolution,
} from '../prostudio.xstate.types';
import { uploadSolutionFiles } from './helpers';

export const createBot = async (
  ctx: ProStudioContext,
  event: ProStudioEvents
): Promise<{
  botName: string;
  selectedSolution: SelectedSolution;
  solutions: NonNullable<GetSolutionsQuery['admin_']>['solutions'];
  cb: (path?: string) => void;
} | void> => {
  if (event.type !== 'createCustomerBot') {
    return;
  }

  const projectProStudioSettingId = ctx.project?.projectProStudioSetting?.id;
  const { customerId, project } = ctx;
  const projectId = project?.id;

  if (!projectId || !projectProStudioSettingId) {
    throwErrorWithId('Project ID and projectProStudioSettingId are required');
    return;
  }

  if (!customerId) {
    throwErrorWithId('Customer ID is required');
    return;
  }

  const resp = await urqlGql.createSolution({
    projectProStudioSettingId,
    solution: {
      name: event.payload.botName,
    },
    version: event.payload.version,
    createDefaultChannels: true,
  });

  checkForErrors(resp);

  const { data: respData } = resp;
  if (!respData) {
    throwErrorWithId('Failed to create solution');
    return;
  }

  if (event?.payload.files?.length > 0) {
    // Files go INTO the draft. A new solution's only version is its draft today, but the
    // solution names it explicitly (`defaultDraftVersionId`), so that is what is used.
    const created = respData.admin_?.createSolution;
    const solutionVersionId = pickDraftVersion(
      created?.solutionVersions,
      created?.defaultDraftVersionId
    )?.id;
    if (solutionVersionId) {
      void uploadSolutionFiles(solutionVersionId, event.payload.files);
    }
  }

  const solutionsResponse = await urqlGql.getSolutions({
    projectProStudioSettingId,
  });

  checkForErrors(solutionsResponse);

  const { data: solutionsData } = solutionsResponse;

  const solutions = solutionsData?.admin_?.solutions;

  const createdSolution = respData.admin_?.createSolution;

  if (!createdSolution?.id) {
    throwErrorWithId('Created solution ID is required');
    return;
  }

  // Built from the createSolution response instead of Bot Manager's getBotById: v2
  // returns `botSolutionId: null` for every new solution, so the old code threw here
  // even though the solution had been created successfully. A brand-new solution has no
  // media yet, hence no assets lookup.
  return {
    botName: createdSolution.name || '',
    selectedSolution: buildSelectedSolution({
      solution: createdSolution,
      displayName: event.payload.botName || '',
    }),
    solutions,
    cb: event.cb,
  };
};
